<template>
<div class="data-container" :class="{ 'loading': loading }">
    <!-- 容器头部 -->
    <div class="container-header">
        <div class="header-content">
            <!-- 标题区域 -->
            <div class="title-section">
                <div class="title-wrapper">
                    <slot name="icon" v-if="$slots.icon">
                        <i class="icon" :class="icon"></i>
                    </slot>
                    <img v-else-if="icon" :src="icon" class="icon" />
                    <img v-else class="icon" src="@/assets/screen/icon.svg" />
                    <div class="title-text">
                        <h2 class="title">{{ title }}</h2>
                        <span class="subtitle" v-if="subtitle">{{ subtitle }}</span>
                    </div>
                </div>

                <div class="header-slot">
                    <slot name="header"></slot>
                </div>
            </div>

            <!-- 操作区域 -->
            <div class="action-section">
                <!-- <div class="refresh-btn" @click="handleRefresh" title="刷新数据" v-if="refreshable"
               :class="{ 'rotating': isRefreshing }">
            <i class="fas fa-sync-alt"></i>
          </div> -->

                <div class="action-slot" v-if="$slots.action">
                    <slot name="action"></slot>
                </div>

                <div class="settings-btn" @click="$emit('settings')" :title="$t('设置')" v-if="settings">
                    <i class="fas fa-cog"></i>
                </div>
            </div>
        </div>

        <div class="header-line"></div>
    </div>

    <!-- 主内容区域 -->
    <div class="container-body">
        <!-- 默认插槽 -->
        <div class="default-content">
            <slot>
                <!-- 默认内容 -->

            </slot>
        </div>

        <!-- 底部插槽 -->
        <div class="footer-slot" v-if="$slots.footer">
            <slot name="footer"></slot>
        </div>
    </div>

    <!-- 加载遮罩 -->
    <div class="loading-overlay" v-if="loading">
        <div class="loading-content">
            <div class="loading-spinner"></div>
            <span class="loading-text">{{ loadingText }}</span>
        </div>
    </div>

    <!-- 错误提示 -->
    <div class="error-overlay" v-if="error">
        <div class="error-content">
            <i class="fas fa-exclamation-triangle"></i>
            <p>{{ error }}</p>
            <button class="retry-btn" @click="handleRefresh">
                <i class="fas fa-redo"></i> {{ $t('重试') }}
            </button>
        </div>
    </div>

</div>
</template>

<script>
export default {
    name: 'DataContainer',
    props: {
        title: {
            type: String,
            default: ''
        },
        subtitle: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default () {
                return this.$t('暂无数据')
            }
        },
        placeholderIcon: {
            type: String,
            default: 'fas fa-inbox'
        },
        refreshable: {
            type: Boolean,
            default: true
        },
        settings: {
            type: Boolean,
            default: false
        },
        autoRefresh: {
            type: Boolean,
            default: true
        },
        refreshInterval: {
            type: Number,
            default: 30000 // 30秒
        },
        loadingText: {
            type: String,
            default () {
                return this.$t('数据加载中...')
            }
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isRefreshing: false,
            refreshTimer: null,
            lastRefreshTime: null
        }
    },
    computed: {
        formattedRefreshTime() {
            return this.lastRefreshTime ? this.formatTime(this.lastRefreshTime) : ''
        }
    },
    mounted() {
        if (this.autoRefresh && this.refreshable) {
            this.startAutoRefresh()
        }
        this.updateLastRefreshTime()
    },
    beforeDestroy() {
        this.stopAutoRefresh()
    },
    methods: {
        async handleRefresh() {
            if (this.isRefreshing || !this.refreshable) return

            this.isRefreshing = true
            this.$emit('refresh-start')

            try {
                // 触发父组件刷新事件
                await this.$emit('refresh')

                // 调用子组件的loadData方法
                this.callChildrenLoadData()

                this.updateLastRefreshTime()
                this.$emit('refresh-success')

            } catch (error) {
                console.error('数据刷新失败:', error)
                this.$emit('refresh-error', error)

            } finally {
                this.isRefreshing = false
                this.$emit('refresh-end')
            }
        },

        callChildrenLoadData() {
            const findAndCallLoadData = (component) => {
                if (component.loadData && typeof component.loadData === 'function') {
                    component.loadData()
                }

                if (component.$children) {
                    component.$children.forEach(child => {
                        findAndCallLoadData(child)
                    })
                }
            }

            findAndCallLoadData(this)
        },

        startAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer)
            }

            this.refreshTimer = setInterval(() => {
                this.handleRefresh()
            }, this.refreshInterval)
        },

        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer)
                this.refreshTimer = null
            }
        },

        updateLastRefreshTime() {
            this.lastRefreshTime = new Date()
        },

        formatTime(date) {
            if (!date) return ''

            const hours = date.getHours().toString().padStart(2, '0')
            const minutes = date.getMinutes().toString().padStart(2, '0')
            const seconds = date.getSeconds().toString().padStart(2, '0')

            return `${hours}:${minutes}:${seconds}`
        },

        showLoading() {
            this.$emit('update:loading', true)
        },

        hideLoading() {
            this.$emit('update:loading', false)
        },

        setError(message) {
            this.$emit('update:error', message)
        },

        clearError() {
            this.$emit('update:error', '')
        }
    }
}
</script>

<style lang="less" scoped>
.data-container {
    position: relative;
    background: transparent;
    border-radius: 4px;
    backdrop-filter: blur(10px);
    overflow: hidden;
    transition: all 0.3s ease;
    height: 100%;

    &:hover {
        box-shadow:
            0 0 20px rgba(0, 150, 255, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        border-color: rgba(0, 150, 255, 0.5);
    }

    &.loading {
        .container-body {
            opacity: 0.5;
        }
    }

    .container-header {
        padding: 4px 0 4px;
        position: relative;
        border-bottom: 1px solid rgba(0, 150, 255, 0.1);

        .header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;

            .title-section {
                display: flex;
                align-items: center;
                gap: 12px;
                width: 100%;

                .title-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    width: 100%;

                    .icon {
                        font-size: 18px;
                        color: #ffffffff;
                        transition: all 0.3s ease;
                        padding: 4px;
                        width: 30px;
                        height: 30px;
                        border-left: 2px solid rgba(0, 149, 255, 1);
                        background: rgba(255, 255, 255, 0.1);

                    }

                    .title-text {
                        flex-grow: 1;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        background: linear-gradient(90deg, #0084ff28 0%, rgba(255, 255, 255, 0.01) 100%);
                        padding: 4px;
                        min-width: 50%;
                        position: relative;
                        padding-left: 12px;

                                            //   伪类，右边框上下连接短横线
                        &::after {
                            content: '';
                            /* 绝对定位，基于图标右侧 */
                            position: absolute;
                            left: -8px;
                            top: 10%;
                            /* 垂直位置：偏上 */
                            /* 短横样式：宽度、高度、颜色 */
                            width: 8px;
                            height: 2px;
                            background-color: #fff;
                            /* 短横颜色，根据图标调整 */
                            /* 可选：圆角让边缘更柔和 */
                            border-radius: 1px;
                        }

                        &::before {
                            content: '';
                            position: absolute;
                            left: -8px;
                            bottom: 10%;
                            /* 垂直位置：偏下 */
                            /* 与上侧短横样式保持一致 */
                            width: 8px;
                            height: 2px;
                            background-color: #fff;
                            border-radius: 1px;
                        }

                        .title {
                            font-size: 16px;
                            font-weight: 600;
                            color: #ffffffff;
                            margin: 0;
                            letter-spacing: 1px;
                        }

                        .subtitle {
                            font-size: 12px;
                            color: rgba(0, 150, 255, 0.7);
                            font-weight: 400;
                        }
                    }
                }

                .header-slot {
                    display: flex;
                    align-items: center;
                }
            }

            .action-section {
                display: flex;
                align-items: center;
                gap: 8px;

                .refresh-btn,
                .settings-btn {
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    border-radius: 50%;
                    background: rgba(0, 150, 255, 0.1);
                    border: 1px solid rgba(0, 150, 255, 0.2);
                    transition: all 0.3s ease;

                    i {
                        font-size: 14px;
                        color: rgba(0, 150, 255, 0.8);
                        transition: all 0.3s ease;
                    }

                    &:hover {
                        background: rgba(0, 150, 255, 0.2);
                        border-color: rgba(0, 150, 255, 0.4);
                        transform: scale(1.1);

                        i {
                            color: #0096ff;
                        }
                    }

                    &.rotating {
                        animation: rotate 1s linear infinite;
                    }
                }

                .action-slot {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            }
        }

        .header-line {
            height: 1px;
            background: linear-gradient(90deg, transparent, rgba(0, 150, 255, 0.3), transparent);
            margin-top: 4px;
        }
    }

    .container-body {
        padding: 2px;
        position: relative;
        min-height: 100px;
        height: 100%;
        height: calc(100% - 46px);
        /* Adjust based on header height */
        border: 2px solid rgba(180, 180, 180, 0.2);
        box-shadow: inset 0 0 10px rgba(0, 150, 255, 0.1);
        background: rgba(3, 49, 92, 0.15);

        .default-content {
            position: relative;
            z-index: 1;
            height: 100%;
            
        }

        .footer-slot {
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid rgba(0, 150, 255, 0.1);
        }
    }

    .placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 120px;
        color: rgba(0, 150, 255, 0.6);
        text-align: center;

        .placeholder-icon {
            font-size: 48px;
            margin-bottom: 12px;
            color: rgba(0, 150, 255, 0.3);
        }

        .placeholder-text {
            font-size: 14px;
            margin: 0;
        }
    }

    .loading-overlay,
    .error-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 26, 51, 0.9);
        backdrop-filter: blur(5px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
    }

    .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;

        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 2px solid rgba(0, 150, 255, 0.3);
            border-top: 2px solid #0096ff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        .loading-text {
            font-size: 13px;
            color: rgba(0, 150, 255, 0.8);
            letter-spacing: 0.5px;
        }
    }

    .error-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: rgba(255, 100, 100, 0.9);
        text-align: center;

        i {
            font-size: 32px;
        }

        p {
            font-size: 14px;
            margin: 0;
            max-width: 200px;
        }

        .retry-btn {
            padding: 8px 16px;
            background: rgba(0, 150, 255, 0.2);
            border: 1px solid rgba(0, 150, 255, 0.3);
            border-radius: 6px;
            color: #0096ff;
            font-size: 12px;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
                background: rgba(0, 150, 255, 0.3);
                border-color: rgba(0, 150, 255, 0.5);
            }
        }
    }

    .refresh-indicator {
        position: absolute;
        bottom: 8px;
        right: 12px;
        font-size: 11px;
        color: rgba(0, 150, 255, 0.6);
        z-index: 5;
    }

    // 动画效果
    @keyframes rotate {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    @keyframes pulse {

        0%,
        100% {
            opacity: 1;
            transform: scale(1);
        }

        50% {
            opacity: 0.8;
            transform: scale(1.05);
        }
    }

    // 响应式设计
    @media (max-width: 768px) {
        .container-header {
            padding: 12px 15px 10px;
        }

        .container-body {
            padding: 15px;
        }

        .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }

        .action-section {
            align-self: flex-end;
        }
    }
}
</style>
